import { computed, action, observable, makeObservable } from 'mobx'
import { type ITableModelDefault, type IRowDefault } from 'shared/ui/TableNew'
import type { IActionsProps } from 'shared/ui'

const rowBaseMakeObservable = {
  _origin: observable,
  _isDisabled: observable,
  _isCheckboxDisabled: observable,
  _expanded: observable,
  _is_can_expanded: observable,
  _actionsProps: observable,
  _isMoreExpand: observable,
  _loadingMore: observable,
  _ariaLabel: observable,
  _loading: observable,
  _columnsKeys: observable,

  id: computed,
  origin: computed,
  rows: computed,
  expanded: computed,
  isDisabled: computed,
  loadingMore: computed,
  isCheckboxDisabled: computed,
  isMoreExpand: computed,
  ariaLabel: computed,
  actionsProps: computed,
  is_can_expanded: computed,
  actions: computed,
  fields: computed,
  loading: computed,
  withActions: computed,
  columnsKeys: computed,

  syncOrigin: action,
  toggleExpanded: action,
  setLoadingMore: action,
  setAriaLabel: action,
  setActionsProps: action,
  setIsCanExpanded: action,
  setLoading: action,
  setDisabled: action,
  setCheckboxDisabled: action,
  setColumnsKeys: action,
}

export const makeObservableRowBase = (
  _this: object,
  props?: Record<string, typeof action | typeof computed | typeof observable>
) => {
  return makeObservable(
    _this,
    {
      ...rowBaseMakeObservable,
      ...(props || {}),
    },
    {
      autoBind: true,
    }
  )
}

export class RowBase<Model extends ITableModelDefault, TRowC = never> implements IRowDefault {
  private readonly _id: Model['id']
  private _origin: Model
  private _rowsMap: Map<number | string, TRowC> = new Map()
  private _isDisabled = false
  private _isCheckboxDisabled = false
  private _expanded = false
  private _is_can_expanded = false
  private _actionsProps: IActionsProps | null = null
  private _isMoreExpand = false
  private _loadingMore = false
  private _loading = false
  private _ariaLabel = ''

  private _columnsKeys: string[] = []

  constructor(origin: Model) {
    this._id = origin.id
    this._origin = origin
  }

  syncOrigin = (origin: Model) => {
    this._origin = origin
  }

  get loading() {
    return this._loading
  }

  get isCheckboxDisabled() {
    return this._isCheckboxDisabled
  }

  get columnsKeys() {
    return this._columnsKeys
  }

  get fields() {
    return this._columnsKeys.reduce<Record<string, unknown>>((state, key) => {
      state[key] = this._origin[key as keyof Model]

      return state
    }, {})
  }

  get ariaLabel() {
    return this._ariaLabel
  }

  get loadingMore() {
    return this._loadingMore
  }

  get isMoreExpand() {
    return this._isMoreExpand
  }

  get is_can_expanded() {
    return this._is_can_expanded
  }

  get expanded() {
    return this._expanded
  }

  get actionsProps() {
    return this._actionsProps
  }

  get actions() {
    return this._actionsProps?.actions || null
  }

  get isDisabled() {
    return this._isDisabled
  }

  get id() {
    return this._id
  }

  get rows() {
    return Array.from(this._rowsMap.values())
  }

  get origin() {
    return this._origin
  }

  get withActions() {
    return Boolean(this._actionsProps || this.actions)
  }

  setLoading = (value: boolean) => {
    this._loading = value
  }

  setColumnsKeys = (keys: string[]) => {
    this._columnsKeys = keys
  }

  setAriaLabel = (value: string) => {
    this._ariaLabel = value
  }

  setActionsProps = (value: IActionsProps) => {
    this._actionsProps = value
  }

  setIsCanExpanded = (value: boolean) => {
    this._is_can_expanded = value
  }

  toggleExpanded() {
    this._expanded = !this._expanded
  }

  setLoadingMore(value: boolean) {
    this._loadingMore = value
  }

  setDisabled = (value: boolean) => {
    this._isDisabled = value
  }

  setCheckboxDisabled = (value: boolean) => {
    this._isCheckboxDisabled = value
  }
}
